import { createApi, fakeBaseQuery, SkipToken } from "@reduxjs/toolkit/query/react";
import { db, functions } from "@/firebase";
import { httpsCallable } from "firebase/functions";
import { addDoc, collection, deleteDoc, doc, getCountFromServer, getDoc, getDocs, limit, orderBy, query, setDoc, startAfter, updateDoc, where } from "firebase/firestore";
import { pick } from "lodash";
import { addLoadingTask, deleteLoadingTask } from "@/store/uiSlice";
import { RootState } from "@/store/store";
import { updateOrder } from "@/features/order/slice";
import { fetchUsers } from "@/store/usersSlice";
import { LocalStorage } from "@/constants/localStorage";
import { ArchiveOrder, CompletedOrder, ItemStatus, Order, OrderStatus } from "@/types";
import { ApiError } from "@/types/api";
import { DocumentSnapshot } from "firebase/firestore";
import { sanitizeForSearch } from "@/utils/names";
import { subDays } from "date-fns";
import { pickBy } from "lodash";
import { DATE_RANGE, DATE_RANGES } from "@/constants/global";
import { DELIVERY_STATUS_TIMESTAMP_MAP, ORDER_STATUS_TIMESTAMP_MAP } from "@/constants/orders";
import { calculateOrderTotals } from "@/utils/order";

interface Pagination {
  action: "none" | "previous" | "next" | "search" | "filter";
  firstDocument?: DocumentSnapshot;
  lastDocument?: DocumentSnapshot;
  pageSize: number;
  orderNumber?: string;
  query?: string;
  status?: ItemStatus | null;
  name?: string;
  lastname?: string;
  branchId?: string;
}

interface OrdersResponse {
  orders: Order[];
  firstDocument: DocumentSnapshot;
  lastDocument: DocumentSnapshot;
  totalDocuments: number;
}

export const ordersApi = createApi({
  reducerPath: "orders",
  baseQuery: fakeBaseQuery<ApiError>(),
  tagTypes: ["Order"],
  endpoints: (builder) => ({
    fetchOrders: builder.query<OrdersResponse, string | SkipToken>({
      queryFn: async (params: { organizationId: string } & Pagination) => {
        if (!params?.organizationId) {
          return {
            error: {
              status: 400,
              statusText: "Consulta incorrecta",
            },
          };
        }

        const colRef = collection(db, `organizations/${params.organizationId}/orders`);

        // let ordersQuery;
        const queryConstraints = [];

        // SEARCH
        if (params.action === "search" && params.query) {
          let searchTerm = params.query.trim();

          if (searchTerm.startsWith("#")) {
            searchTerm = searchTerm.substring(1);
          }

          if (isNaN(Number(searchTerm)) || searchTerm === "") {
            // IS CLIENT
            searchTerm = searchTerm.toLowerCase();
            const [name, lastname] = searchTerm.split(" ");
            const sanitizedName = sanitizeForSearch(name);

            if (name && lastname) {
              const sanitizedLastname = sanitizeForSearch(lastname);
              queryConstraints.push(where("client.sanitizedName", "==", sanitizedName), where("client.sanitizedLastname", "==", sanitizedLastname));
            } else if (name) {
              queryConstraints.push(where("client.sanitizedName", "==", sanitizedName));
            }
          } else {
            // IS ORDER - Try both number and string for robustness
            const numValue = Number(searchTerm);
            queryConstraints.push(where("number", "in", [numValue, searchTerm]));
          }
        }

        // NONE
        if (params.action === "none") {
          queryConstraints.push(orderBy("createdAt", "asc"));
        }

        // ON NEXT
        if (params?.action === "next") {
          queryConstraints.push(startAfter(params?.lastDocument));

          if (params?.status) {
            queryConstraints.push(where("status", "==", params.status));
          }
        }

        // ON PREVIOUS
        if (params?.action === "previous") {
          queryConstraints.push(orderBy("createdAt", "desc"), startAfter(params?.firstDocument));

          if (params?.status) {
            queryConstraints.push(where("status", "==", params.status));
          }
        }

        // FILTER BY STATUS
        if (params?.action === "filter" && params.status) {
          queryConstraints.push(where("status", "==", params.status), orderBy("createdAt", "asc"));
        }

        const sizedOrdersQuery = query(colRef, ...queryConstraints, limit(params.pageSize));

        const ordersSnapshots = await getDocs(sizedOrdersQuery);

        const orders = await ordersSnapshots.docs.reduce((acc, doc: any) => {
          return [...acc, { ...doc.data(), id: doc.id }];
        }, [] as Order[]);

        const firstDocument = params.action === "previous" ? ordersSnapshots.docs.reverse()[0] : ordersSnapshots.docs[0];
        const lastDocument =
          params.action === "previous" ? ordersSnapshots.docs.reverse()[ordersSnapshots.docs.reverse().length - 1] : ordersSnapshots.docs[ordersSnapshots.docs.length - 1];

        let countSnapshot;

        if (params.status || params.query) {
          const countConstraints = queryConstraints.filter((c) => c.type === "where");
          countSnapshot = await getCountFromServer(query(colRef, ...countConstraints));
        } else {
          countSnapshot = await getCountFromServer(colRef);
        }

        if (orders.length) {
          return {
            data: {
              orders,
              firstDocument,
              lastDocument,
              totalDocuments: countSnapshot.data().count,
            },
          };
        }

        return {
          error: {
            status: 404,
            statusText: "Accesos no encontrados",
          },
        };
      },
      providesTags: ["Order"],
    }),
    getOrderById: builder.query({
      queryFn: async ({ organizationId, orderId }: { organizationId: string; orderId: Order["id"] }, { dispatch }) => {
        const TASK = "Orden por Id";
        try {
          dispatch(addLoadingTask(TASK));
          let docRef = doc(db, `organizations/${organizationId}/orders/${orderId}`);
          let snapshot = await getDoc(docRef);

          if (!snapshot.exists()) {
            docRef = doc(db, `organizations/${organizationId}/completedOrders/${orderId}`);
            snapshot = await getDoc(docRef);
          }

          if (!snapshot.exists()) {
            docRef = doc(db, `organizations/${organizationId}/archivedOrders/${orderId}`);
            snapshot = await getDoc(docRef);
          }

          if (snapshot.exists()) {
            const responsePayload = { ...snapshot.data(), id: snapshot.id };

            if (snapshot.data().createdBy) {
              dispatch(fetchUsers([snapshot.data().createdBy]));
            }
            dispatch(updateOrder(responsePayload));
            return { data: responsePayload };
          } else {
            return {
              error: {
                message: "Order not found",
                status: 404,
              },
            };
          }
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
    }),
    getArchivedOrderById: builder.query({
      queryFn: async ({ organizationId, orderId }: { organizationId: string; orderId: Order["id"] }, { dispatch }) => {
        const docRef = doc(db, `organizations/${organizationId}/archivedOrders/${orderId}`);

        const snapshot = await getDoc(docRef);

        if (snapshot.exists()) {
          const responsePayload = { ...snapshot.data(), id: snapshot.id };

          if (snapshot.data().createdBy) {
            dispatch(fetchUsers([snapshot.data().createdBy]));
          }
          dispatch(updateOrder(responsePayload));
          return { data: responsePayload };
        }

        return {
          error: {
            message: "Order not found",
            status: 404,
          },
        };
      },
    }),
    updateOrderStatus: builder.mutation({
      queryFn: async ({ organizationId, orderId, status }: { organizationId: string; orderId: string; status: OrderStatus }, { getState, dispatch }) => {
        const TASK = "Actualizar Orden";

        try {
          dispatch(addLoadingTask(TASK));

          const store: any = getState() as RootState;

          if (!store.auth?.user?.uid || !organizationId || !orderId || !status) {
            throw new Error("Invalid Payload");
          }

          const docRef = doc(db, `organizations/${String(organizationId)}/orders/${String(orderId)}`);

          const timestampKey = ORDER_STATUS_TIMESTAMP_MAP[status];

          const payload: any = {
            status,
            lastUpdatedBy: store.auth.user.uid,
            lastUpdatedAt: Date.now(),
          };

          if (timestampKey) {
            payload[timestampKey] = Date.now();
          }

          await updateDoc(docRef, pickBy(payload, v => v !== undefined));

          return { data: payload };
        } catch (error) {
          console.error(error);
          return { error: error as any };
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      invalidatesTags: ["Order"],
    }),
    updateDeliveryStatus: builder.mutation({
      queryFn: async ({ organizationId, orderId, status }: { organizationId: string; orderId: string; status: DeliveryStatus }, { getState, dispatch }) => {
        const TASK = "Actualizar Orden";

        try {
          dispatch(addLoadingTask(TASK));

          const store: any = getState() as RootState;

          if (!store.auth?.user?.uid || !organizationId || !orderId || !status) {
            throw new Error("Invalid Payload");
          }

          const docRef = doc(db, `organizations/${String(organizationId)}/orders/${String(orderId)}`);

          const timestampKey = DELIVERY_STATUS_TIMESTAMP_MAP[status];

          const payload: any = {
            status,
            lastUpdatedBy: store.auth.user.uid,
            lastUpdatedAt: Date.now(),
          };

          if (timestampKey) {
            payload[timestampKey] = Date.now();
          }

          await updateDoc(docRef, pickBy(payload, v => v !== undefined));

          return { data: payload };
        } catch (error) {
          console.error(error);
          return { error: error as any };
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      invalidatesTags: ["Order"],
    }),
    updateOrder: builder.mutation<{ orderedAt: number } | ApiError, { order: Partial<Order>; organizationId: string }>({
      queryFn: async ({ order, organizationId }, { getState, dispatch }) => {
        const TASK = "Actualizar Orden";

        try {
          dispatch(addLoadingTask(TASK));

          const store: any = getState() as RootState;

          const ls = localStorage.getItem(LocalStorage.USER);
          const lsUser = JSON.parse(ls ?? "{}");

          if (!lsUser || !organizationId || !order?.id) {
            throw new Error("Invalid Payload");
          }
          const docRef = doc(db, `organizations/${String(organizationId)}/orders/${String(order.id)}`);

          const ALLOWED_FIELDS = [
            "lastUpdatedBy",
            "lastUpdatedAt",
            "status",
            "items",
            "tableId",
            "subtotal",
            "taxes",
            "areTipsIncluded",
            "total",
            "paymentDetails",
            "deliveryLocation",
            "client",
            "tipPercentage",
            "number",
            "flow",
            "source",
            "branch",
            "scheduleTime",
            "orderedAt",
            "approvedAt",
            "rejectedAt",
            "sendingAt",
            "completedAt",
            "serviceFee",
            "deliveryFee",
          ];

          const sanitizedName = sanitizeForSearch(order.client?.name ?? "");
          const sanitizedLastname = sanitizeForSearch(order.client?.lastname ?? "");

          const timestampKey = ORDER_STATUS_TIMESTAMP_MAP[order.status || ""];

          const payload: any = {
            ...pick(order, ALLOWED_FIELDS),
            client: order.client
              ? {
                ...order.client,
                sanitizedName,
                sanitizedLastname,
              }
              : undefined,
            lastUpdatedBy: store?.auth?.user?.uid || lsUser,
            lastUpdatedAt: Date.now(),
            orderedAt: order.orderedAt || (order.status === OrderStatus.REVIEWING ? Date.now() : undefined),
          };

          if (order.items) {
            const { subtotal, taxes, total } = calculateOrderTotals(order.items, order.tipPercentage, order.areTipsIncluded);
            payload.subtotal = subtotal;
            payload.taxes = taxes;
            payload.total = total;
          }

          if (timestampKey) {
            payload[timestampKey] = Date.now();
          }

          const sanitizedPayload = pickBy(payload, v => v !== undefined);

          await updateDoc(docRef, sanitizedPayload);

          dispatch(updateOrder(sanitizedPayload));

          return { data: sanitizedPayload };
        } catch (error) {
          console.error(error);
          return { error: error as any };
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      invalidatesTags: ["Order"],
    }),
    //TODO: Finish this mutation
    addOrder: builder.mutation({
      queryFn: async ({ organizationId, order }: { organizationId: string; order: Partial<Order> }, { getState, dispatch }) => {
        const TASK = "Agregar Orden";

        if (!organizationId || !order) {
          return {
            error: {
              status: 400,
              statusText: "Consulta Inválida",
            },
          };
        }

        const store = getState() as RootState;
        try {
          dispatch(addLoadingTask(TASK));

          const collectionRef = collection(db, `organizations/${String(organizationId)}/orders`);

          const ALLOWED_FIELDS = [
            "createdBy",
            "createdAt",
            "status",
            "items",
            "tableId",
            "subtotal",
            "taxes",
            "areTipsIncluded",
            "total",
            "number",
            "user",
            "flow",
            "source",
            "branch",
            "scheduleTime",
            "orderedAt",
            "approvedAt",
            "rejectedAt",
            "sendingAt",
            "completedAt",
            "serviceFee",
            "deliveryFee",
            "client",
            "deliveryLocation",
          ];

          const status = order.status || OrderStatus.DRAFT;

          const timestampKey = ORDER_STATUS_TIMESTAMP_MAP[status];

          const payload: any = {
            ...pick(order, ALLOWED_FIELDS),
            createdBy: order.createdBy || store.auth?.user?.uid || "anonymous",
            createdAt: order.createdAt || Date.now(),
            status,
          };

          if (order.items) {
            const { subtotal, taxes, total } = calculateOrderTotals(order.items, order.tipPercentage, order.areTipsIncluded);
            payload.subtotal = subtotal;
            payload.taxes = taxes;
            payload.total = total;
          }

          if (timestampKey) {
            payload[timestampKey] = Date.now();
          }

          const sanitizedPayload = pickBy(payload, v => v !== undefined);
          const response = await addDoc(collectionRef, sanitizedPayload);
          return { data: { ...sanitizedPayload, id: response.id } };
        } catch (error) {
          console.error(error);
          return { error: error as any };
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      invalidatesTags: ["Order"],
    }),
    fetchWeeklyArchiveOrders: builder.query<(ArchiveOrder | CompletedOrder)[], { organizationId: string | SkipToken; dateRange: DATE_RANGE | SkipToken }>({
      queryFn: async ({ organizationId, dateRange }) => {
        const numberOfDays: number = DATE_RANGES[dateRange].days;
        const sevenDaysAgo = subDays(new Date(), numberOfDays).getTime();

        // Archived orders
        const archivedColRef = collection(db, `organizations/${organizationId}/archivedOrders`);

        const archivedQuery = query(archivedColRef, where("archivedAt", ">=", sevenDaysAgo), orderBy("archivedAt", "desc"));

        const archivedResponse = await getDocs(archivedQuery).then((querySnapshot) => {
          return querySnapshot.docs.reduce((acc, doc: any) => {
            return [...acc, { ...doc.data(), id: doc.id }];
          }, [] as ArchiveOrder[]);
        });

        // Completed orders
        const completedColRef = collection(db, `organizations/${organizationId}/completedOrders`);

        const completedQuery = query(completedColRef, where("completedAt", ">=", sevenDaysAgo), orderBy("completedAt", "desc"));

        const completedResponse = await getDocs(completedQuery).then((querySnapshot) => {
          return querySnapshot.docs.reduce((acc, doc: any) => {
            return [...acc, { ...doc.data(), id: doc.id }];
          }, [] as ArchiveOrder[]);
        });

        return { data: [...archivedResponse, ...completedResponse] };
      },
      providesTags: ["Order"],
    }),
    archiveOrder: builder.mutation({
      queryFn: async ({ order, orderId, organizationId }: { order: ArchiveOrder; orderId: ArchiveOrder["id"]; organizationId: string }, { getState, dispatch }) => {
        const TASK = "Archivar Orden";
        try {
          dispatch(addLoadingTask(TASK));

          const store: any = getState() as RootState;

          const ls = localStorage.getItem(LocalStorage.USER);
          const lsUser = JSON.parse(ls ?? "{}");

          if (!lsUser || !organizationId || !orderId) {
            throw new Error("Invalid Payload");
          }

          const ALLOWED_FIELDS = [
            "archivedAt",
            "archivedBy",
            "lastUpdatedBy",
            "lastUpdatedAt",
            "status",
            "items",
            "tableId",
            "subtotal",
            "taxes",
            "areTipsIncluded",
            "total",
            "paymentDetails",
            "deliveryLocation",
            "client",
            "number",
            "flow",
            "branch",
            "scheduleTime",
            "source",
            "serviceFee",
            "deliveryFee",
          ];

          const archivedOrdersRef = doc(db, `organizations/${String(organizationId)}/archivedOrders/${String(orderId)}`);

          const sanitizedName = sanitizeForSearch(order.client?.name ?? "");
          const sanitizedLastname = sanitizeForSearch(order.client?.lastname ?? "");

          const payload = {
            ...pick(order, ALLOWED_FIELDS),
            client: {
              ...order.client,
              sanitizedName,
              sanitizedLastname,
            },
            lastUpdatedBy: store.auth?.user?.uid ?? lsUser,
            lastUpdatedAt: Date.now(),
            archivedBy: store.auth?.user?.uid ?? lsUser,
            archivedAt: Date.now(),
            orderedAt: Date.now(),
            status: OrderStatus.ARCHIVED,
          };

          if (order.items) {
            const { subtotal, taxes, total } = calculateOrderTotals(order.items, order.tipPercentage, order.areTipsIncluded);
            payload.subtotal = subtotal;
            payload.taxes = taxes;
            payload.total = total;
          }


          await setDoc(archivedOrdersRef, payload);

          const unarchivedOrdersRef = doc(db, `organizations/${String(organizationId)}/orders/${String(orderId)}`);
          await deleteDoc(unarchivedOrdersRef);

          return { data: payload };
        } catch (error) {
          console.error(error);
          return { error: error as any };
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
    }),
    completeOrder: builder.mutation({
      queryFn: async ({ order, orderId, organizationId }: { order: CompletedOrder; orderId: CompletedOrder["id"]; organizationId: string }, { getState, dispatch }) => {
        const TASK = "Orden Completada";

        try {
          dispatch(addLoadingTask(TASK));

          const store: any = getState() as RootState;

          const ls = localStorage.getItem(LocalStorage.USER);
          const lsUser = JSON.parse(ls ?? "{}");

          if (!order || !organizationId || !orderId || (!store?.auth?.user?.uid && !lsUser)) {
            throw new Error("Invalid Payload");
          }
          const ALLOWED_FIELDS = [
            "status",
            "items",
            "tableId",
            "subtotal",
            "taxes",
            "areTipsIncluded",
            "total",
            "paymentDetails",
            "deliveryLocation",
            "client",
            "number",
            "flow",
            "branch",
            "scheduleTime",
            "source",
            "serviceFee",
            "deliveryFee",

            // timestamps
            "createdAt",
            "createdBy",
            "orderedAt",
            "orderedBy",
            "approvedAt",
            "approvedBy",
            "rejectedAt",
            "rejectedBy",
            "sendingAt",
            "sendingBy",
            "completedAt",
            "completedBy",
            "lastUpdatedBy",
            "lastUpdatedAt",
            "paidAt",
            "paidBy",
          ];

          const completedOrdersRef = doc(db, `organizations/${String(organizationId)}/completedOrders/${String(orderId)}`);

          const payload = {
            ...pick(order, ALLOWED_FIELDS),
            lastUpdatedBy: store.auth?.user?.uid ? store.auth?.user?.uid : lsUser,
            lastUpdatedAt: Date.now(),
            completedBy: store.auth?.user?.uid ? store.auth?.user?.uid : lsUser,
            completedAt: Date.now(),
            status: OrderStatus.COMPLETED,
          };

          if (order.items) {
            const { subtotal, taxes, total } = calculateOrderTotals(order.items, order.tipPercentage ?? 0, order.areTipsIncluded ?? false);
            payload.subtotal = subtotal;
            payload.taxes = taxes;
            payload.total = total;
          }


          await setDoc(completedOrdersRef, payload);
          const uncompletedOrdersRef = doc(db, `organizations/${String(organizationId)}/orders/${String(orderId)}`);
          await deleteDoc(uncompletedOrdersRef);

          return { data: payload };
        } catch (error) {
          console.error(error);
          return { error: error as any };
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      invalidatesTags: ["Order"],
    }),
    rejectOrder: builder.mutation({
      queryFn: async ({ order, orderId, organizationId, rejectedReason = "Rechazada" }: { order: Order; orderId: Order["id"]; organizationId: string; rejectedReason?: string }, { getState, dispatch }) => {
        const TASK = "Orden Rechazada";

        try {
          dispatch(addLoadingTask(TASK));

          const store: any = getState() as RootState;

          const ls = localStorage.getItem(LocalStorage.USER);
          const lsUser = JSON.parse(ls ?? "{}");

          if (!order || !organizationId || !orderId || (!store?.auth?.user?.uid && !lsUser)) {
            throw new Error("Invalid Payload");
          }
          const ALLOWED_FIELDS = [
            "status",
            "items",
            "tableId",
            "subtotal",
            "taxes",
            "areTipsIncluded",
            "total",
            "paymentDetails",
            "deliveryLocation",
            "client",
            "number",
            "flow",
            "branch",
            "scheduleTime",
            "source",
            "serviceFee",
            "deliveryFee",

            // timestamps
            "createdAt",
            "createdBy",
            "orderedAt",
            "orderedBy",
            "approvedAt",
            "approvedBy",
            "rejectedAt",
            "rejectedBy",
            "rejectedReason",
            "sendingAt",
            "sendingBy",
            "completedAt",
            "completedBy",
            "lastUpdatedBy",
            "lastUpdatedAt",
            "paidAt",
            "paidBy",
          ];

          const completedOrdersRef = doc(db, `organizations/${String(organizationId)}/completedOrders/${String(orderId)}`);

          const payload = {
            ...pick(order, ALLOWED_FIELDS),
            lastUpdatedBy: store.auth?.user?.uid ? store.auth?.user?.uid : lsUser,
            lastUpdatedAt: Date.now(),
            rejectedBy: store.auth?.user?.uid ? store.auth?.user?.uid : lsUser,
            rejectedAt: Date.now(),
            completedBy: store.auth?.user?.uid ? store.auth?.user?.uid : lsUser,
            completedAt: Date.now(), // Añadimos esto para que aparezca en el grid ordenado por fecha
            status: OrderStatus.REJECTED,
            rejectedReason,
          };

          if (order.items) {
            const { subtotal, taxes, total } = calculateOrderTotals(order.items, order.tipPercentage ?? 0, order.areTipsIncluded ?? false);
            payload.subtotal = subtotal;
            payload.taxes = taxes;
            payload.total = total;
          }


          await setDoc(completedOrdersRef, payload);
          const uncompletedOrdersRef = doc(db, `organizations/${String(organizationId)}/orders/${String(orderId)}`);
          await deleteDoc(uncompletedOrdersRef);

          return { data: payload };
        } catch (error) {
          console.error(error);
          return { error: error as any };
        } finally {
          dispatch(deleteLoadingTask(TASK));
        }
      },
      invalidatesTags: ["Order"],
    }),
    //fetch completed orders exactly same as fetchArchivedOrders structure but from completedOrders collection
    fetchCompletedOrders: builder.query<OrdersResponse, any | SkipToken>({
      queryFn: async (params: { organizationId: string } & Pagination) => {
        try {
          if (!params?.organizationId) {
            return {
              error: {
                status: 400,
                statusText: "Consulta incorrecta",
              },
            };
          }

          const colRef = collection(db, `organizations/${params.organizationId}/completedOrders`);
          const queryConstraints = [];

          // SEARCH
          if (params.action === "search" && params.query) {
            let searchTerm = params.query.trim();

            if (searchTerm.startsWith("#")) {
              searchTerm = searchTerm.substring(1);
            }

            if (isNaN(Number(searchTerm)) || searchTerm === "") {
              // IS CLIENT
              searchTerm = searchTerm.toLowerCase();
              const [name, lastname] = searchTerm.split(" ");
              const sanitizedName = sanitizeForSearch(name);

              if (name && lastname) {
                const sanitizedLastname = sanitizeForSearch(lastname);
                queryConstraints.push(where("client.sanitizedName", "==", sanitizedName), where("client.sanitizedLastname", "==", sanitizedLastname));
              } else if (name) {
                queryConstraints.push(where("client.sanitizedName", "==", sanitizedName));
              }
            } else {
              // IS ORDER - Try both number and string
              const numValue = Number(searchTerm);
              queryConstraints.push(where("number", "in", [numValue, searchTerm]));
            }
          }

          // NONE
          if (params.action === "none") {
            queryConstraints.push(orderBy("completedAt", "asc"));
          }

          // ON NEXT
          if (params?.action === "next") {
            queryConstraints.push(startAfter(params?.lastDocument));
          }

          // ON PREVIOUS
          if (params?.action === "previous") {
            queryConstraints.push(orderBy("completedAt", "desc"), startAfter(params?.firstDocument));
          }

          const sizedOrdersQuery = query(colRef, ...queryConstraints, limit(params.pageSize));
          const ordersSnapshots = await getDocs(sizedOrdersQuery);

          const orders = ordersSnapshots.docs.reduce((acc, doc: any) => {
            return [...acc, { ...doc.data(), id: doc.id }];
          }, [] as Order[]);

          const firstDocument = params.action === "previous" ? ordersSnapshots.docs.reverse()[0] : ordersSnapshots.docs[0];
          const lastDocument =
            params.action === "previous" ? ordersSnapshots.docs.reverse()[ordersSnapshots.docs.reverse().length - 1] : ordersSnapshots.docs[ordersSnapshots.docs.length - 1];

          let countSnapshot;
          if (params.query) {
            const countConstraints = queryConstraints.filter((c: any) => c.type === "where");
            countSnapshot = await getCountFromServer(query(colRef, ...countConstraints));
          } else {
            countSnapshot = await getCountFromServer(colRef);
          }

          return {
            data: {
              orders,
              firstDocument: firstDocument || null,
              lastDocument: lastDocument || null,
              totalDocuments: countSnapshot.data().count,
            },
          };
        } catch (error) {
          console.error("fetchCompletedOrders error:", error);
          return { error: { status: 500, statusText: "Internal Server Error", data: error } as any };
        }
      },
      providesTags: ["Order"],
    }),
    fetchArchivedOrders: builder.query<OrdersResponse, any | SkipToken>({
      queryFn: async (params: { organizationId: string } & Pagination) => {
        try {
          if (!params?.organizationId) {
            return {
              error: {
                status: 400,
                statusText: "Consulta incorrecta",
              },
            };
          }

          const colRef = collection(db, `organizations/${params.organizationId}/archivedOrders`);
          const queryConstraints = [];

          // SEARCH
          if (params.action === "search" && params.query) {
            let searchTerm = params.query.trim();

            if (searchTerm.startsWith("#")) {
              searchTerm = searchTerm.substring(1);
            }

            if (isNaN(Number(searchTerm)) || searchTerm === "") {
              // IS CLIENT
              searchTerm = searchTerm.toLowerCase();
              const [name, lastname] = searchTerm.split(" ");
              const sanitizedName = sanitizeForSearch(name);

              if (name && lastname) {
                const sanitizedLastname = sanitizeForSearch(lastname);
                queryConstraints.push(where("client.sanitizedName", "==", sanitizedName), where("client.sanitizedLastname", "==", sanitizedLastname));
              } else if (name) {
                queryConstraints.push(where("client.sanitizedName", "==", sanitizedName));
              }
            } else {
              // IS ORDER - Try both number and string
              const numValue = Number(searchTerm);
              queryConstraints.push(where("number", "in", [numValue, searchTerm]));
            }
          }

          // NONE
          if (params.action === "none") {
            queryConstraints.push(orderBy("archivedAt", "asc"));
          }

          // ON NEXT
          if (params?.action === "next") {
            queryConstraints.push(startAfter(params?.lastDocument));
          }

          // ON PREVIOUS
          if (params?.action === "previous") {
            queryConstraints.push(orderBy("archivedAt", "desc"), startAfter(params?.firstDocument));
          }

          const sizedOrdersQuery = query(colRef, ...queryConstraints, limit(params.pageSize));
          const ordersSnapshots = await getDocs(sizedOrdersQuery);

          const orders = ordersSnapshots.docs.reduce((acc, doc: any) => {
            return [...acc, { ...doc.data(), id: doc.id }];
          }, [] as Order[]);

          const firstDocument = params.action === "previous" ? ordersSnapshots.docs.reverse()[0] : ordersSnapshots.docs[0];
          const lastDocument =
            params.action === "previous" ? ordersSnapshots.docs.reverse()[ordersSnapshots.docs.reverse().length - 1] : ordersSnapshots.docs[ordersSnapshots.docs.length - 1];

          let countSnapshot;
          if (params.query) {
            const countConstraints = queryConstraints.filter((c: any) => c.type === "where");
            countSnapshot = await getCountFromServer(query(colRef, ...countConstraints));
          } else {
            countSnapshot = await getCountFromServer(colRef);
          }

          return {
            data: {
              orders,
              firstDocument: firstDocument || null,
              lastDocument: lastDocument || null,
              totalDocuments: countSnapshot.data().count,
            },
          };
        } catch (error) {
          console.error(error);
          return { error: { status: 500, statusText: "Internal Server Error", data: error } as any };
        }
      },
      providesTags: ["Order"],
    }),
    fetchOrganizationOrders: builder.query<OrdersResponse, any | SkipToken>({
      queryFn: async (params: { organizationId: string } & Pagination) => {
        try {
          if (!params?.organizationId) {
            return {
              error: {
                status: 400,
                statusText: "Consulta incorrecta",
              },
            };
          }

          const colRef = collection(db, `organizations/${params.organizationId}/completedOrders`);
          const queryConstraints = [];

          // FILTERS
          const hasFilters = !!(params.name || params.lastname || params.orderNumber || params.branchId);

          if (params.name) {
            queryConstraints.push(where("client.sanitizedName", "==", params.name));
          }
          if (params.lastname) {
            queryConstraints.push(where("client.sanitizedLastname", "==", params.lastname));
          }
          if (params.orderNumber) {
            const numValue = Number(params.orderNumber);
            if (!isNaN(numValue)) {
              // Some orders might have 'number' as a string or number. Handle both.
              queryConstraints.push(where("number", "in", [numValue, params.orderNumber.toString()]));
            } else {
              queryConstraints.push(where("number", "==", params.orderNumber));
            }
          }
          if (params.branchId) {
            queryConstraints.push(where("branch.id", "==", params.branchId));
          }

          // NONE / DEFAULT SORT
          // Only apply default sort if there are no specific filters to avoid requiring multiple composite indexes
          if ((params.action === "none" || params.action === "filter") && !hasFilters) {
            queryConstraints.push(orderBy("completedAt", "desc"));
          }

          // ON NEXT
          if (params?.action === "next" && params.lastDocument) {
            if (!hasFilters) {
              queryConstraints.push(orderBy("completedAt", "desc"));
            }
            queryConstraints.push(startAfter(params.lastDocument));
          }

          // ON PREVIOUS
          if (params?.action === "previous" && params.firstDocument) {
            if (!hasFilters) {
              queryConstraints.push(orderBy("completedAt", "asc"));
            }
            queryConstraints.push(startAfter(params.firstDocument));
          }

          const sizedOrdersQuery = query(colRef, ...queryConstraints, limit(params.pageSize));
          const ordersSnapshots = await getDocs(sizedOrdersQuery);

          let docs = ordersSnapshots.docs;
          if (params.action === "previous") {
            docs = [...docs].reverse();
          }

          const orders = docs.reduce((acc, doc) => {
            return [...acc, { ...doc.data(), id: doc.id } as Order];
          }, [] as Order[]);

          const firstDocument = docs[0];
          const lastDocument = docs[docs.length - 1];

          let countSnapshot;

          if (params.name || params.lastname || params.orderNumber || params.branchId) {
            const countConstraints = [];
            if (params.name) countConstraints.push(where("client.sanitizedName", "==", params.name));
            if (params.lastname) countConstraints.push(where("client.sanitizedLastname", "==", params.lastname));
            if (params.orderNumber) {
              const numValue = Number(params.orderNumber);
              if (!isNaN(numValue)) {
                countConstraints.push(where("number", "in", [numValue, params.orderNumber.toString()]));
              } else {
                countConstraints.push(where("number", "==", params.orderNumber));
              }
            }
            if (params.branchId) {
              countConstraints.push(where("branch.id", "==", params.branchId));
            }
            countSnapshot = await getCountFromServer(query(colRef, ...countConstraints));
          } else {
            countSnapshot = await getCountFromServer(colRef);
          }

          return {
            data: {
              orders,
              firstDocument: firstDocument || null,
              lastDocument: lastDocument || null,
              totalDocuments: countSnapshot.data().count,
            },
          };
        } catch (error) {
          console.error("fetchOrganizationOrders error:", error);
          return { error: { status: 500, statusText: "Internal Server Error", data: error } as any };
        }
      },
      providesTags: ["Order"],
    }),
    rescheduleOrderTimeout: builder.mutation({
      queryFn: async ({ organizationId, orderId }: { organizationId: string; orderId: string }) => {
        try {
          const reschedule = httpsCallable<{ organizationId: string; orderId: string }, { success: boolean; newTaskName: string }>(functions, "rescheduleOrderTimeout");
          const result = await reschedule({ organizationId, orderId });
          return { data: result.data };
        } catch (error) {
          console.error(error);
          return { error: { status: 500, statusText: "Internal Server Error", data: error } as any };
        }
      },
      invalidatesTags: ["Order"],
    }),
  }),
});

export const {
  useLazyFetchOrdersQuery,
  useFetchOrdersQuery,
  useGetOrderByIdQuery,
  useGetArchivedOrderByIdQuery,
  useUpdateOrderStatusMutation,
  useUpdateDeliveryStatusMutation,
  useAddOrderMutation,
  useUpdateOrderMutation,
  useFetchWeeklyArchiveOrdersQuery,
  useArchiveOrderMutation,
  useCompleteOrderMutation,
  useRejectOrderMutation,
  useLazyFetchCompletedOrdersQuery,
  useLazyFetchArchivedOrdersQuery,
  useLazyFetchOrganizationOrdersQuery,
  useRescheduleOrderTimeoutMutation,
} = ordersApi;
